import { useState } from "react";
import { Plus, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export function ViewsTabs({ views, activeViewId, onSelectView, onCreateView, onRenameView, onDeleteView }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canDelete = views.length > 1;

  const submit = () => {
    if (!name.trim()) return;
    onCreateView(name.trim());
    setName("");
    setOpen(false);
  };

  const startRename = (v) => {
    setRenamingId(v.id);
    setRenameValue(v.name);
  };

  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && renamingId) onRenameView(renamingId, trimmed);
    setRenamingId(null);
  };

  return (
    <div className="flex items-center gap-1 px-2 h-10 min-w-0 overflow-x-auto" data-testid="views-tabs-bar">
      {views.map((v) => (
        <div key={v.id} className="group relative flex items-center">
          {renamingId === v.id ? (
            <Input
              data-testid={`view-rename-input-${v.id}`}
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setRenamingId(null);
              }}
              className="h-7 w-32 text-xs text-slate-900 bg-white"
            />
          ) : (
            <button
              data-testid={`view-tab-${v.id}`}
              onClick={() => onSelectView(v.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap border transition-all duration-150 ${
                activeViewId === v.id
                  ? "bg-blue-50/80 text-blue-700 border-blue-200 shadow-sm"
                  : "bg-transparent text-slate-600 border-transparent hover:bg-slate-200/60 hover:text-slate-900"
              }`}
            >
              {v.name}
            </button>
          )}
          {renamingId !== v.id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`view-tab-menu-${v.id}`}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-slate-700 transition-opacity"
                >
                  <MoreVertical size={13} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem data-testid={`view-rename-menu-item-${v.id}`} onClick={() => startRename(v)}>
                  Rename
                </DropdownMenuItem>
                <div title={!canDelete ? "A project always keeps at least one view" : undefined}>
                  <DropdownMenuItem
                    data-testid={`view-delete-menu-item-${v.id}`}
                    disabled={!canDelete}
                    onClick={() => canDelete && setDeleteTarget(v)}
                    className={canDelete ? "text-red-600 focus:text-red-600" : ""}
                  >
                    Delete
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
      <button data-testid="add-view-button" onClick={() => setOpen(true)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md">
        <Plus size={14} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-view-dialog">
          <DialogHeader>
            <DialogTitle>New View</DialogTitle>
            <DialogDescription>Add another diagram view to this project.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="create-view-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="View name"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} data-testid="create-view-submit-button">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-view-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This only removes this diagram layout. Elements and relationships stay in your model and in any other views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-view-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-view-confirm-button"
              onClick={() => {
                onDeleteView(deleteTarget.id);
                setDeleteTarget(null);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
